const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;
const DAYS_IN_WEEK = 7;

function startOfLocalDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function formatClockTime(date: Date): string {
  // toLocaleTimeString yields "8:12 PM"; the chat surface wants lowercase meridiem.
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
}

/**
 * Format a message timestamp for the chat transcript separators.
 *
 * Granularity widens with age so recent messages read as a bare clock time
 * and older ones gain just enough date context to disambiguate:
 *
 * | when                        | example              |
 * | --------------------------- | -------------------- |
 * | today                       | `2:20 pm`            |
 * | 1–6 days ago (this week)    | `Thursday 11:43 am`  |
 * | 7+ days ago                 | `5/23 8:12 pm`       |
 *
 * Day buckets are computed from local-midnight boundaries, so "today" flips
 * at the viewer's midnight rather than on a rolling 24-hour window. `now` is
 * injectable for deterministic tests.
 */
export default function formatMessageTimestamp(date: Date, now: Date = new Date()): string {
  const dayDiff = Math.round((startOfLocalDay(now) - startOfLocalDay(date)) / (24 * 60 * 60 * 1000));
  const clock = formatClockTime(date);

  if (dayDiff <= 0) {
    return clock;
  }

  if (dayDiff < DAYS_IN_WEEK) {
    return `${WEEKDAY_NAMES[date.getDay()]} ${clock}`;
  }

  return `${date.getMonth() + 1}/${date.getDate()} ${clock}`;
}
